import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { formatUK } from "../../lib/ukdate";
import { MagnifyingGlass, Plus, FilePdf } from "@phosphor-icons/react";

const STATE = (d) => (d.working ? d.working.status : d.published ? "published" : "draft");
const CHIP = { published: "bg-emerald-50 text-emerald-800", draft: "bg-slate-100 text-slate-600", review: "bg-amber-50 text-amber-800", approved: "bg-blue-50 text-blue-800" };

// Simplified searchable Manager list — every row opens the one-screen workspace.
export function DocumentsList({ onCreate, onUploadPdf }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [state, setState] = useState("");
  const nav = useNavigate();
  useEffect(() => { api.get("/policies/authoring/drafts").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  const kinds = useMemo(() => [...new Set((list || []).map((d) => d.kind))].sort(), [list]);
  const rows = (list || []).filter((d) => (!q || `${d.title} ${d.ref}`.toLowerCase().includes(q.toLowerCase()))
    && (!kind || d.kind === kind) && (!state || STATE(d) === state || (state === "working" && d.working)));
  const sel = "h-10 rounded-md border border-slate-200 bg-white px-2 text-sm";
  return (
    <div className="flex flex-col gap-3" data-testid="ws-documents-list">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-3 text-slate-400" />
          <Input data-testid="ws-doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or reference…" className="pl-9 bg-white" />
        </div>
        <select data-testid="ws-doc-kind" className={sel} value={kind} onChange={(e) => setKind(e.target.value)}><option value="">All types</option>{kinds.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}</select>
        <select data-testid="ws-doc-state" className={sel} value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">Any status</option><option value="published">Published, no draft</option><option value="working">Has a working draft</option><option value="draft">Never published</option></select>
        <button data-testid="ws-new-doc" onClick={onCreate} className="h-10 px-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold inline-flex items-center gap-1"><Plus size={15} />New document</button>
        <button data-testid="ws-new-pdf" onClick={onUploadPdf} className="h-10 px-3 rounded-md border border-slate-300 bg-white text-sm font-semibold inline-flex items-center gap-1"><FilePdf size={15} />Upload PDF / toolbox talk</button>
      </div>
      <div className="text-xs text-slate-500">{list === null ? "Loading…" : `${rows.length} of ${list.length} documents`}</div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y">
        {rows.map((d) => (
          <Link key={d.id} to={`/policy-manager/doc/${d.id}`} data-testid={`ws-doc-${d.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50" onClick={(e) => { e.preventDefault(); nav(`/policy-manager/doc/${d.id}`); }}>
            <span className="font-mono text-xs text-slate-400 w-24 shrink-0 truncate">{d.ref}</span>
            <span className="flex-1 min-w-0 truncate text-sm font-semibold text-slate-800">{d.title}</span>
            <span className="hidden sm:inline text-[11px] text-slate-400 w-24">{d.kind?.replace(/_/g, " ")}</span>
            <span className="hidden md:inline text-[11px] text-slate-400 w-28">{d.review_date ? `Review ${formatUK(d.review_date)}` : ""}</span>
            <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${CHIP[STATE(d)] || CHIP.draft}`}>{d.working ? `${d.working.status} ${d.working.revision}` : d.published ? `Rev ${d.published.revision}` : "draft"}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
